import { toIsoDate } from './dateHelpers';
import { TIPOS_TITULOS, getTitulosInstruccion } from './instruccionesTitulos';

// Estados que NO cuentan como duplicado: una instrucción rechazada o eliminada se
// vuelve a cargar legítimamente, y avisar en ese caso sería puro ruido.
export const ESTADOS_NO_DUPLICABLES = ['Eliminada', 'Rechazada'];

const TIPOS_FCI = ['Suscripcion FCI', 'Rescate FCI'];

// Normaliza para comparar: sin espacios ni diferencias de mayúsculas, de modo que
// "MEGAQM PESOS CLASE B" y "Megaqm Pesos Clase B" caigan en la misma clave.
const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, '').toUpperCase();

/**
 * Día en el que el registro se ejecuta realmente. Las instrucciones programadas se
 * comparan contra su día de desbloqueo, no contra el día en que se cargaron.
 * Un candidato recién armado en el formulario todavía no tiene createdAt: es hoy.
 */
export const getFechaEfectiva = (item) => {
    if (item?.fechaProgramada && (item.estado === 'Programada' || item.esProgramada)) {
        return item.fechaProgramada;
    }
    const createdAt = item?.createdAt;
    if (createdAt?.toDate) return toIsoDate(createdAt.toDate());
    if (createdAt instanceof Date) return toIsoDate(createdAt);
    if (typeof createdAt === 'string' || typeof createdAt === 'number') {
        const parsed = new Date(createdAt);
        if (!isNaN(parsed.getTime())) return toIsoDate(parsed);
    }
    return toIsoDate(new Date());
};

// El instrumento depende del tipo: fondo para FCI, especie para títulos. Los
// movimientos de dinero (Ingreso/Egreso) no tienen instrumento; ahí el par
// cliente + tipo + moneda + día ya es señal suficiente. Una transferencia puede
// llevar varias especies: devuelve una por cada una.
const getInstrumentos = (instr) => {
    if (TIPOS_FCI.includes(instr?.tipo)) return [normalize(instr?.fci)];
    if (TIPOS_TITULOS.includes(instr?.tipo)) {
        const especies = getTitulosInstruccion(instr).map((titulo) => normalize(titulo.especie));
        return especies.length > 0 ? [...new Set(especies)] : [''];
    }
    return [''];
};

/**
 * Claves de agrupación de instrucciones: una por instrumento. Dos registros que
 * comparten alguna clave son "posible duplicado": una transferencia de AL30 y
 * GD30 choca con otra del mismo día que mueva AL30. Deliberadamente NO incluye
 * el monto: el caso típico es cargar primero un monto estimado y después el exacto.
 */
export const getInstruccionDuplicateKeys = (instr) => {
    if (!instr?.tipo || !instr?.nroComitente) return null;
    const fecha = getFechaEfectiva(instr);
    return getInstrumentos(instr).map((instrumento) => [
        normalize(instr.tipo),
        normalize(instr.nroComitente),
        normalize(instr.moneda),
        instrumento,
        fecha,
    ].join('|'));
};

/**
 * Clave de agrupación de operaciones. Incluye plazo y mercado porque una compra
 * C.I. y una 24hs, o la misma especie repartida entre BYMA y SENEBI, son
 * operaciones distintas y legítimas.
 */
export const getOperacionDuplicateKey = (op) => {
    if (!op?.operacion || !op?.nroComitente || !op?.especie) return null;
    return [
        normalize(op.operacion),
        normalize(op.nroComitente),
        normalize(op.especie),
        normalize(op.plazo),
        normalize(op.moneda),
        normalize(op.mercado),
        getFechaEfectiva(op),
    ].join('|');
};

// `getKey` puede devolver una clave, una lista de claves o null.
const toKeyList = (keys) => {
    if (!keys) return [];
    return [...new Set(Array.isArray(keys) ? keys : [keys])].filter(Boolean);
};

/**
 * Registros ya existentes que comparten alguna clave con el candidato.
 * @param {object} candidate registro que se está por guardar
 * @param {Array} existing registros ya cargados (con id)
 * @param {Function} getKey getInstruccionDuplicateKeys | getOperacionDuplicateKey
 * @param {{ excludeId?: string }} options excludeId evita que un registro en edición se marque contra sí mismo
 */
export const findDuplicates = (candidate, existing = [], getKey, { excludeId } = {}) => {
    const keys = new Set(toKeyList(getKey(candidate)));
    if (keys.size === 0) return [];
    return existing.filter((item) => {
        if (!item) return false;
        if (excludeId && item.id === excludeId) return false;
        if (ESTADOS_NO_DUPLICABLES.includes(item.estado)) return false;
        return toKeyList(getKey(item)).some((key) => keys.has(key));
    });
};

/**
 * Mapa docId -> otros registros que comparten su clave. Solo incluye registros
 * que estén en un grupo de 2 o más. Alimenta el chip de advertencia de la tabla.
 */
export const buildDuplicateGroups = (items = [], getKey) => {
    const byKey = new Map();
    items.forEach((item) => {
        if (!item?.id || ESTADOS_NO_DUPLICABLES.includes(item.estado)) return;
        toKeyList(getKey(item)).forEach((key) => {
            if (!byKey.has(key)) byKey.set(key, []);
            byKey.get(key).push(item);
        });
    });

    // Un registro con varias claves puede compartir cada una con un registro
    // distinto: sus pares son la unión, sin repetir.
    const paresPorId = new Map();
    byKey.forEach((group) => {
        if (group.length < 2) return;
        group.forEach((item) => {
            if (!paresPorId.has(item.id)) paresPorId.set(item.id, new Map());
            const pares = paresPorId.get(item.id);
            group.forEach((other) => {
                if (other.id !== item.id) pares.set(other.id, other);
            });
        });
    });

    const groups = new Map();
    paresPorId.forEach((pares, id) => groups.set(id, [...pares.values()]));
    return groups;
};

/**
 * Diferencia entre el monto que se está cargando y el ya cargado.
 * Devuelve null si alguno de los dos no es un monto comparable (rescates totales, títulos).
 */
export const getMontoDelta = (montoNuevo, montoExistente) => {
    const nuevo = Number(montoNuevo);
    const existente = Number(montoExistente);
    if (!Number.isFinite(nuevo) || !Number.isFinite(existente)) return null;
    if (nuevo === 0 || existente === 0) return null;
    const absoluto = nuevo - existente;
    return { absoluto, porcentaje: (absoluto / existente) * 100 };
};
